import * as R from 'ramda'
import i18n from 'i18next'
import moment from 'moment'

import { templateLicenceTypes } from '../../utils/Globals/SurveyFieldOptions'

const ALLOWED_SURVEY_TYPES = ['EV', 'CEandPE', 'EM']

const createSettingsComponents = (settingStore, type, store, settingsFields, isTemplate = false) => {
    const { survey, countries } = store
    const { metadata, closed: isClosed } = survey
    const { industrySpecificInfo } = metadata.productInfo
    const { industry, productType } = industrySpecificInfo
    const { taxonomy } = metadata
    const { surveyType, respondentType } = taxonomy
    const prodTypes = ['CEEE', 'CECE']
    const deleteButtonLabel = surveyType === 'EM' ? i18n.t('settings_delete_maxbi') : i18n.t('settings_delete_survey')
    const { showCeVenueDetails } = settingStore
    const templateFlag = process.env.NEW_TEMPLATING
    const isExploriTemplate = survey.metadata.clientId === 1
    const eventCloseYear = moment.isMoment(settingStore.eventCloseDate)
        ? settingStore.eventCloseDate.year()
        : ''
    const templateParams = settingStore.getTemplateParams()

    const fields = [
        {
            type: 'select',
            compName: 'primaryLanguage',
            name: 'primaryLanguage',
            options: survey.metadata.isRapidSurvey
                ? settingStore.selectedLanguages : settingStore.primaryLanguageOptions,
            value: settingStore.primaryLanguageOption,
            onChange: settingStore.changePrimaryLanguage,
            show: true,
            label: surveyType !== 'EM'
                ? i18n.t('settings_survey_primary_languages')
                : i18n.t('settings_survey_primary_languages_EM'),
            disabled: settingStore.surveyClosed,
        },
        {
            type: 'dynamicComponentSets',
            compName: 'languages',
            name: 'languages',
            options: settingStore.selectedLanguages,
            show: settingStore.selectedLanguages.length > 1 && !survey.metadata.isRapidSurvey,
            topic: i18n.t('settings_survey_languages'),
            style: 'language-checkbox-set',
            components: settingStore.selectedLanguages.map((language) => (
                {
                    type: 'checkbox',
                    compName: 'languages',
                    name: language.label,
                    defaultChecked: language.checked,
                    checked: R.pathOr('', ['primaryLanguageOption', 'value'], settingStore) === language.value
                        ? true : undefined,
                    value: language.value,
                    onChange: language.value === R.pathOr('', ['primaryLanguageOption', 'value'], settingStore)
                        ? null : (e) => settingStore.changeSelectedLanguages(e, language),
                    show: true,
                    label: language.label,
                    disabled: settingStore.surveyClosed,
                }
            )),
        },
        {
            type: 'custom',
            compName: 'surveyStatus',
            name: 'surveyStatus',
            label: survey.isMaxbi() ? i18n.t('maxbi_survey_status') : i18n.t('s_edit_form_survey_status'),
            bannerLabel: settingStore.surveyStatus.label,
            style: settingStore.surveyStatus.style,
            show: true,
            icon: settingStore.surveyStatus.icon,
        },
        {
            type: 'custom',
            compName: 'noOfResponses',
            name: 'noOfResponses',
            label: i18n.t('s_edit_form_num_responses'),
            icon: 'fa-regular fa-circle-check',
            bannerLabel: settingStore.noOfResponses,
            show: surveyType !== 'EM',
        },
        {
            type: 'input',
            compName: 'surveyName',
            name: 'surveyName',
            label: surveyType !== 'EM' ? i18n.t('settings_survey_name') : i18n.t('settings_entry_name'),
            value: settingStore.surveyName,
            show: true,
            onChange: (e) => settingStore.setSurveyName(e),
            onBlur: () => settingStore.hanldeAllowEdit('surveyName'),
        },
        {
            type: 'select',
            compName: 'event',
            name: 'event',
            options: [{ label: settingStore.eventName, value: settingStore.eventName }],
            value: { label: settingStore.eventName, value: settingStore.eventName },
            show: true,
            label: surveyType !== 'EM'
                ? i18n.t('views.survey.wizard.EV.form.product.label')
                : i18n.t('settings_product_label_EM'),
            lockedDropdown: true,
            showDisabled: true,
            disabled: true,
            openMenuOnClick: false,
        },
        {
            type: 'select',
            compName: 'topic',
            options: [{ label: settingStore.topic, value: settingStore.topic }],
            name: 'topic',
            value: { label: settingStore.topic, value: settingStore.topic },
            show: true,
            label: i18n.t('views.survey.wizard.all.form.survey_type.label'),
            lockedDropdown: true,
            showDisabled: true,
            disabled: true,
            openMenuOnClick: false,
        },
        {
            type: 'select',
            compName: 'templateLicenceType',
            options: templateLicenceTypes(),
            name: 'templateLicenceType',
            value: templateLicenceTypes().find(({ value }) => value === survey.metadata.taxonomy.templateLicenceType),
            show: isTemplate,
            label: i18n.t('views.survey.wizard.all.form.template_licence_type.label'),
            lockedDropdown: false,
            showDisabled: false,
            disabled: false,
            openMenuOnClick: true,
            onChange: settingStore.onLicenceTypeChange,
        },
        {
            type: 'select',
            compName: 'audience',
            options: [{ label: settingStore.audience, value: settingStore.audience }],
            name: 'audience',
            value: { label: settingStore.audience, value: settingStore.audience },
            show: true,
            label: i18n.t('views.survey.wizard.all.form.respondent_type.label'),
            lockedDropdown: true,
            showDisabled: true,
            disabled: true,
            openMenuOnClick: false,
        },
        {
            type: 'flexFields',
            compName: 'eventOpenAndClose',
            name: 'eventOpenAndClose',
            show: surveyType !== 'GE',
            components: [{
                type: 'date',
                compName: 'eventOpenDate',
                name: 'eventOpenDate',
                label: i18n.t('settings_event_open_date'),
                value: settingStore.eventOpenDate,
                maxDate: settingStore.eventCloseDate,
                show: surveyType !== 'GE',
                onBlur: settingStore.onDateChange,
                allowManualDateEdit: true,
                dateFormat: 'DD/MM/YYYY',
                disabled: settingStore.surveyClosed,
            },
            {
                type: 'date',
                compName: 'eventCloseDate',
                name: 'eventCloseDate',
                label: i18n.t('settings_event_close_date'),
                value: settingStore.eventCloseDate,
                minDate: settingStore.eventOpenDate,
                show: surveyType !== 'GE',
                onBlur: settingStore.onDateChange,
                allowManualDateEdit: true,
                dateFormat: 'DD/MM/YYYY',
                disabled: settingStore.surveyClosed,
            }],
        },
        {
            type: 'select',
            compName: 'noVisitors',
            name: 'noVisitors',
            options: settingStore.numVisitors,
            value: settingStore.numVisitorsOrExhibitors,
            onChange: settingStore.changeNumVisitors,
            show: industry === 'EV' && surveyType === 'PE' && respondentType === 'VI',
            label: i18n.t('settings_no_vistors'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'select',
            compName: 'noExhibitors',
            name: 'noExhibitors',
            options: settingStore.numExhibitors,
            value: settingStore.numVisitorsOrExhibitors,
            onChange: settingStore.changeNumVisitors,
            show: industry === 'EV' && surveyType === 'PE' && respondentType === 'EX',
            label: i18n.t('settings_no_exhibitors'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'select',
            compName: 'countryEV',
            name: 'countryEV',
            options: countries,
            value: settingStore.selectedCountry,
            onChange: settingStore.changeCountry,
            show: industry === 'EV' && surveyType === 'PE',
            label: i18n.t('settings_country'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'select',
            compName: 'cityEV',
            name: 'cityEV',
            options: settingStore.cities,
            value: settingStore.selectedCity,
            onChange: settingStore.changeCity,
            show: industry === 'EV' && surveyType === 'PE',
            label: i18n.t('settings_city'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'input',
            compName: 'cityOtherEV',
            name: 'cityOtherEV',
            show: industry === 'EV' && settingStore.selectedCity.value === 'Other, please specify...',
            label: i18n.t('settings_city_other'),
            value: settingStore.cityotherEV,
            allowEdit: !settingStore.allowEditCityotherEV,
            hanldeAllowEdit: () => settingStore.hanldeAllowEdit('cityOther'),
            onChange: (e) => settingStore.setCityother(e),
            onBlur: () => settingStore.onCityOtherEnter(),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'select',
            compName: 'venueEV',
            name: 'venueEV',
            options: settingStore.venues,
            value: settingStore.selectedVenue,
            onChange: settingStore.changeVenue,
            show: industry === 'EV' && surveyType === 'PE',
            label: i18n.t('settings_venue'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'input',
            compName: 'venueOtherEV',
            name: 'venueOtherEV',
            label: i18n.t('settings_venue_other_EV'),
            value: settingStore.venueOther,
            allowEdit: !settingStore.allowEditVenueotherEV,
            hanldeAllowEdit: () => settingStore.hanldeAllowEdit('venueOther'),
            show: settingStore.selectedVenue.value === -1,
            onChange: (e) => settingStore.setVenueother(e),
            onBlur: () => settingStore.hanldeAllowEdit('venueOther'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'select',
            compName: 'eventFormat',
            name: 'eventFormat',
            options: settingStore.eventTypes,
            value: settingStore.selectedEventFormat,
            onChange: settingStore.changeEventFormat,
            show: industry === 'CE' && prodTypes.includes(productType),
            label: i18n.t('settings_evenet_format'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'select',
            compName: 'countryCE',
            name: 'countryCE',
            options: countries,
            value: settingStore.selectedCountry,
            onChange: settingStore.changeCountry,
            show: showCeVenueDetails(),
            label: i18n.t('settings_country'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'select',
            compName: 'stateCE',
            name: 'stateCE',
            options: settingStore.stateCE,
            value: settingStore.selectedStateCE,
            onChange: settingStore.changeStateCE,
            show: settingStore.stateCE.length > 0 && showCeVenueDetails(),
            label: i18n.t('settings_state'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'select',
            compName: 'cityCE',
            name: 'cityCE',
            options: settingStore.cities,
            value: settingStore.selectedCity,
            onChange: settingStore.changeCity,
            show: showCeVenueDetails(),
            label: i18n.t('settings_city', 'City'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'input',
            compName: 'cityOtherCE',
            name: 'cityOtherCE',
            label: i18n.t('settings_city_other', 'City - other specify'),
            value: settingStore.cityotherEV,
            allowEdit: !settingStore.allowEditCityotherEV,
            hanldeAllowEdit: () => settingStore.hanldeAllowEdit('cityOther'),
            show: industry === 'CE' && settingStore.selectedCity.value === 'Other, please specify...',
            onChange: (e) => settingStore.setCityother(e),
            onBlur: () => settingStore.onCityOtherEnter(),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'input',
            compName: 'venueOtherCE',
            name: 'venueOtherCE',
            label: i18n.t('settings_venue_other'),
            value: settingStore.venueOther,
            show: industry === 'CE'
                && (
                    (surveyType === 'EM' && !prodTypes.includes(productType))
                    || (surveyType === 'PE' && prodTypes.includes(productType))
                ),
            onChange: (e) => settingStore.setVenueother(e),
            onBlur: settingStore.validateVenueOther,
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'select',
            compName: 'defaultComparison',
            name: 'defaultComparison',
            options: settingStore.defaultcomparison,
            value: settingStore.selectedDefaultcomparison,
            onChange: settingStore.changeDefaultcomparison,
            show: surveyType !== 'GE' && settingStore.defaultcomparison.length > 1,
            label: i18n.t('default_yr_on_yr_survey'),
            disabled: settingStore.surveyClosed,
            lockedDropdown: settingStore.surveyClosed,
        },
        {
            type: 'flexFields',
            compName: 'surveyCloseDateTime',
            name: 'surveyCloseDateTime',
            show: true,
            components: [{
                type: 'date',
                compName: 'surveyCloseDate',
                name: 'surveyCloseDate',
                label: i18n.t('survey_closing_date'),
                value: settingStore.surveycloseDate,
                show: true,
                onBlur: settingStore.onDateChange,
                calendarCss: 'surveyClose-date-popper',
                allowManualDateEdit: true,
                dateFormat: 'DD/MM/YYYY',
                disabled: settingStore.surveyClosed,
            },
            {
                type: 'time',
                compName: 'surveyCloseTime',
                name: 'surveyCloseTime',
                label: i18n.t('survey_closing_time'),
                placeholder: i18n.t('survey_closing_time'),
                value: settingStore.surveycloseDate,
                show: true,
                onBlur: settingStore.onDateChange,
                calendarCss: 'surveyClose-time-popper',
                disabled: settingStore.surveyClosed,
            }],
        },
        {
            type: 'dynamicComponentSets',
            compName: 'customMergeField',
            name: 'customMergeField',
            show: settingStore.mergeFieldOptions.length > 1,
            style: 'mergeFeilds',
            components: settingStore.mergeFieldOptions.map((option) => {
                if (option.mergefieldType === 'SB') {
                    return ({
                        type: 'select',
                        compName: 'customMergeField',
                        name: 'customMergeField',
                        options: JSON.parse(option.options).map((item) => ({
                            value: item[store.langcode],
                            label: item[store.langcode],
                        })),
                        defaultValue: {
                            value: R.pathOr('', [option.mergefieldId, store.langcode], survey.mergefields),
                            label: R.pathOr('', [option.mergefieldId, store.langcode], survey.mergefields),
                        },
                        onChange: (obj) => settingStore
                            .changeSelectMergeFields(option.mergefieldId, obj.value),
                        show: true,
                        label: option.description,
                    })
                }
                return ({
                    type: 'input',
                    compName: 'customMergeField',
                    name: 'customMergeField',
                    defaultValue: R.pathOr('', [option.mergefieldId, store.langcode], survey.mergefields),
                    show: true,
                    label: option.description,
                    onChange: (e) => settingStore
                        .changeMergeFields(option.mergefieldId, e.target.value),
                    onBlur: settingStore.updateMergeFields,

                })
            }),
        },
        {
            type: 'switch',
            compName: 'primarySurvey',
            name: 'primarySurvey',
            show: surveyType !== 'GE',
            label: surveyType !== 'EM' ? i18n.t('settings_primary_survey') : i18n.t('settings_primary_maxbi'),
            text: `${surveyType !== 'EM'
                ? i18n.t('settings_primary_survey_text')
                : i18n.t('settings_primary_maxbi_entry_text')} ${settingStore.eventName} ${eventCloseYear}`,
            toggleSwitch: settingStore.togglePrimarySwitch,
            state: !settingStore.isPrimary,
            disabled: settingStore.surveyClosed,
            tooltipText: i18n.t('settings_primary_survey_hover_text'),
        },
        {
            type: 'switch',
            compName: 'test',
            name: 'test',
            show: true,
            label: surveyType !== 'EM' ? i18n.t('settings_test_survey') : i18n.t('settings_test_survey_EM'),
            text: surveyType !== 'EM' ? i18n.t('isTest_survey') : i18n.t('settings_test_entry_placeholder_EM'),
            toggleSwitch: settingStore.changeIsTest,
            state: !settingStore.isTest,
            disabled: settingStore.surveyClosed,
            tooltipText: i18n.t('settings_test_survey_hover_text'),
        },
        {
            type: 'flexFields',
            compName: 'buttonFields',
            name: 'buttonFields',
            show: true,
            components: [{
                type: 'button',
                compName: isClosed ? 'openButton' : 'closeButton',
                name: isClosed ? 'openButton' : 'closeButton',
                icon: `fa-regular ${isClosed ? 'fa-calendar' : 'fa-calendar-xmark'}`,
                cssClass: `button-large ${isClosed ? 'tertiary' : 'primary'}`,
                handleClick: isClosed
                    ? () => settingStore.toggleOpenSurveyModal(true)
                    : settingStore.closeSurvey,
                show: surveyType !== 'EM',
                label: isClosed
                    ? i18n.t('settings_open_survey', 'Open survey')
                    : i18n.t('settings_close_survey', 'Close survey'),
            },
            {
                type: 'button',
                compName: 'deleteButton',
                name: 'deleteButton',
                icon: `fa-regular ${survey.metadata.status !== 'Archived' ? 'fa-circle-xmark' : 'fa-refresh'}`,
                cssClass: `button-large ${survey.metadata.status !== 'Archived' ? 'close-button-settings' : 'primary'}`,
                handleClick: survey.metadata.status !== 'Archived' ? settingStore.deleteSurveyModelOpen : settingStore.restoreSurvey,
                show: true,
                label: survey.metadata.status !== 'Archived' ? deleteButtonLabel : i18n.t('restore'),
            }],
        },
        {
            type: 'templateAccessAccordion',
            compName: 'templateAccessAccordion',
            name: 'templateAccessAccordion',
            show: templateFlag && isTemplate && !isExploriTemplate,
        },
        {
            type: 'templateCustomTags',
            compName: 'templateCustomTags',
            name: 'templateCustomTags',
            show: templateFlag && isTemplate,
            templateParams,
        },
        {
            type: 'button',
            compName: 'publishButton',
            name: 'publishButton',
            icon: `fa-regular ${store.survey.metadata.isTemplatePublished ? 'fa-paper-plane-top' : 'fa-paper-plane-top'}`,
            cssClass: `uc-button regular ${store.survey.metadata.isTemplatePublished ? 'tertiary' : 'primary'} publishButton`,
            handleClick: () => { settingStore.showPublishConfirmationModal = true },
            show: templateFlag && isTemplate,
            label: store.survey.metadata.isTemplatePublished ? i18n.t('settings_unpublish') : i18n.t('settings_publish'),
        },
    ]
    const allowedFields = {
        otherFields: [],
        upperPanel: [],
        languageOptions: [],
        buttonPanel: [],
    }

    if (ALLOWED_SURVEY_TYPES.includes(type)) {
        const usingFields = isTemplate ? settingsFields.template : settingsFields[type]
        usingFields.forEach(
            (fieldName) => {
                if (typeof fieldName === 'object') {
                    Object.keys(fieldName).forEach((key) => {
                        fieldName[key].forEach((name) => {
                            allowedFields[key].push(fields
                                .find((field) => (field.compName === name)))
                        })
                    })
                } else {
                    allowedFields.otherFields
                        .push(fields.find((field) => (field.compName === fieldName)))
                }
            },
        )
    }
    return allowedFields
}

export default createSettingsComponents
